import { EventBus } from '../core/Events';
import type { DifficultyDef } from '../data/difficulty';
import type { EffectSpec } from '../data/items';

/**
 * Oyuncu hayat sistemi: can, açlık, stamina ve durum efektleri.
 * Zorluk çarpanları buradan uygulanır.
 */

export interface ActiveEffect {
  id: string;
  remaining: number;
  magnitude: number;
}

export interface StatsSave {
  health: number;
  maxHealth: number;
  hunger: number;
  stamina: number;
  effects: ActiveEffect[];
}

type StatsEvents = {
  changed: void;
  damaged: { amount: number; source: string };
  died: { cause: string };
  'effect:added': ActiveEffect;
  'effect:expired': { id: string };
  starving: void;
};

export const MAX_HUNGER = 100;
export const MAX_STAMINA = 100;

export class Stats {
  readonly events = new EventBus<StatsEvents>();
  maxHealth = 100;
  health = 100;
  hunger = MAX_HUNGER;
  stamina = MAX_STAMINA;
  private effects = new Map<string, ActiveEffect>();
  private hungerAccum = 0;
  private starveAccum = 0;
  private regenAccum = 0;
  private venomAccum = 0;
  dead = false;

  reset(): void {
    this.health = this.maxHealth;
    this.hunger = MAX_HUNGER;
    this.stamina = MAX_STAMINA;
    this.effects.clear();
    this.hungerAccum = 0;
    this.starveAccum = 0;
    this.regenAccum = 0;
    this.dead = false;
    this.events.emit('changed', undefined);
  }

  /** Efekt çarpanı; efekt yoksa 1 (nötr) döner. */
  modifier(id: string): number {
    return this.effects.get(id)?.magnitude ?? 1;
  }

  hasEffect(id: string): boolean {
    return this.effects.has(id);
  }

  activeEffects(): ActiveEffect[] {
    return [...this.effects.values()];
  }

  addEffect(spec: EffectSpec): void {
    const existing = this.effects.get(spec.id);
    const eff: ActiveEffect = {
      id: spec.id,
      // Aynı efekt tekrar içilirse süre uzar, şiddet en yükseği kalır
      remaining: Math.max(spec.duration, existing?.remaining ?? 0),
      magnitude: Math.max(spec.magnitude, existing?.magnitude ?? 0),
    };
    this.effects.set(spec.id, eff);
    this.events.emit('effect:added', eff);
    this.events.emit('changed', undefined);
  }

  clearEffect(id: string): void {
    if (this.effects.delete(id)) {
      this.events.emit('effect:expired', { id });
      this.events.emit('changed', undefined);
    }
  }

  update(
    dt: number,
    ctx: { moving: boolean; sprinting: boolean; difficulty: DifficultyDef; inHazard?: { effect: string; magnitude: number } | null },
  ): void {
    if (this.dead) return;

    // --- efekt süreleri ---
    for (const eff of [...this.effects.values()]) {
      if (eff.id === 'venom' || eff.id === 'cold' || eff.id === 'slow') {
        // çevresel efektler alanda kalındıkça yenilenir
      }
      eff.remaining -= dt;
      if (eff.remaining <= 0) {
        this.effects.delete(eff.id);
        this.events.emit('effect:expired', { id: eff.id });
      }
    }

    // --- çevresel tehlike (bataklık/buz/mor biyom) ---
    if (ctx.inHazard) {
      const resist = this.hasEffect('venomResist') && ctx.inHazard.effect === 'venom';
      if (!resist) {
        this.addEffectQuiet(ctx.inHazard.effect, 4, ctx.inHazard.magnitude);
      }
    }

    // --- zehir hasarı ---
    if (this.hasEffect('venom')) {
      this.venomAccum += dt;
      if (this.venomAccum >= 2) {
        this.venomAccum = 0;
        this.damage(2 * this.modifier('venom'), 'zehir');
      }
    }

    // --- açlık (soğuk: titreme enerji harcar, açlık daha hızlı düşer) ---
    const satiety = this.hasEffect('satiety') ? this.modifier('satiety') : 1;
    const coldPenalty = this.hasEffect('cold') ? 1 + Math.min(0.6, this.modifier('cold')) : 1;
    const hungerPerSec = (ctx.sprinting ? 0.55 : ctx.moving ? 0.28 : 0.12) * ctx.difficulty.hungerRate * satiety * coldPenalty;
    this.hungerAccum += hungerPerSec * dt;
    if (this.hungerAccum >= 1) {
      const whole = Math.floor(this.hungerAccum);
      this.hungerAccum -= whole;
      this.hunger = Math.max(0, this.hunger - whole);
      this.events.emit('changed', undefined);
    }

    // --- açlıktan hasar / can yenilenmesi ---
    if (this.hunger <= 0) {
      this.regenAccum = 0;
      this.starveAccum += dt;
      if (this.starveAccum >= 4) {
        this.starveAccum = 0;
        this.events.emit('starving', undefined);
        this.damage(3, 'açlık');
      }
    } else {
      this.starveAccum = 0;
      if (this.hunger > MAX_HUNGER * 0.6 && this.health < this.maxHealth && ctx.difficulty.regen > 0) {
        this.regenAccum += dt * ctx.difficulty.regen;
        if (this.regenAccum >= 3) {
          this.regenAccum = 0;
          this.heal(1);
        }
      }
    }

    // --- stamina ---
    if (ctx.sprinting && ctx.moving) {
      this.stamina = Math.max(0, this.stamina - dt * 18 * ctx.difficulty.staminaDrain);
      this.events.emit('changed', undefined);
    } else if (this.stamina < MAX_STAMINA) {
      let rate = this.hunger <= 0 ? 3 : 12;
      if (this.hasEffect('cold')) rate *= 1 - Math.min(0.5, this.modifier('cold'));
      this.stamina = Math.min(MAX_STAMINA, this.stamina + dt * rate);
      this.events.emit('changed', undefined);
    }
  }

  /** Süreyi yenileyen sessiz efekt (event spam'i olmadan). */
  private addEffectQuiet(id: string, duration: number, magnitude: number): void {
    const cur = this.effects.get(id);
    if (cur) {
      cur.remaining = Math.max(cur.remaining, duration);
      cur.magnitude = Math.max(cur.magnitude, magnitude);
    } else {
      this.addEffect({ id, duration, magnitude });
    }
  }

  damage(amount: number, source: string, defense = 0): void {
    if (this.dead || amount <= 0) return;
    // Savunma azalan verimle hasarı düşürür (yüksek zırhta bile sıfırlanmaz)
    const reduced = amount * (1 - Math.min(0.8, defense / (defense + 60)));
    this.health = Math.max(0, this.health - reduced);
    this.events.emit('damaged', { amount: reduced, source });
    this.events.emit('changed', undefined);
    if (this.health <= 0) {
      this.dead = true;
      this.events.emit('died', { cause: source });
    }
  }

  heal(amount: number): void {
    if (this.dead) return;
    this.health = Math.min(this.maxHealth, this.health + amount);
    this.events.emit('changed', undefined);
  }

  eat(hunger: number, health = 0): void {
    this.hunger = Math.min(MAX_HUNGER, this.hunger + hunger);
    if (health > 0) this.heal(health);
    else if (health < 0) this.damage(-health, 'çiğ yemek');
    this.events.emit('changed', undefined);
  }

  canSprint(): boolean {
    return this.stamina > 5 && this.hunger > 0;
  }

  toSave(): StatsSave {
    return {
      health: this.health,
      maxHealth: this.maxHealth,
      hunger: this.hunger,
      stamina: this.stamina,
      effects: this.activeEffects(),
    };
  }

  loadSave(data: Partial<StatsSave> | undefined): void {
    const n = (v: unknown, def: number, min: number, max: number) => {
      const x = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(x) ? Math.max(min, Math.min(max, x)) : def;
    };
    this.maxHealth = n(data?.maxHealth, 100, 20, 400);
    this.health = n(data?.health, this.maxHealth, 0, this.maxHealth);
    if (this.health <= 0) this.health = this.maxHealth;
    this.hunger = n(data?.hunger, MAX_HUNGER, 0, MAX_HUNGER);
    this.stamina = n(data?.stamina, MAX_STAMINA, 0, MAX_STAMINA);
    this.effects.clear();
    for (const e of data?.effects ?? []) {
      if (!e || typeof e.id !== 'string') continue;
      if (!Number.isFinite(e.remaining) || e.remaining <= 0) continue;
      this.effects.set(e.id, { id: e.id, remaining: e.remaining, magnitude: Number.isFinite(e.magnitude) ? e.magnitude : 1 });
    }
    this.dead = false;
    this.events.emit('changed', undefined);
  }
}
